import { afterNextRender, ChangeDetectionStrategy, Component, effect, inject, Injector, output, signal, untracked } from '@angular/core';
import { BriefProgressEnum } from '@/app/common/general-types/briefs.dto';
import { ManageBriefTabsService } from '../../service/manage-brief-tabs/manage-brief-tabs.service';
import { BriefingSectionKey } from './briefing.helpers';
import { BriefingAdmin } from './molecules/briefing-admin/briefing-admin';
import { BriefingHireMessage } from './molecules/briefing-hire-message/briefing-hire-message';
import { BriefingBasic } from './sections/briefing-basic';
import { BriefingCompliance } from './sections/briefing-compliance';
import { BriefingCreative } from './sections/briefing-creative';
import { BriefingCriteria } from './sections/briefing-criteria';
import { BriefingDeliverables } from './sections/briefing-deliverables';
import { BriefingDelivery } from './sections/briefing-delivery';
import { BriefingPay } from './sections/briefing-pay';
import { BriefingScreening } from './sections/briefing-screening';

const FLASH_MS = 1200;

/**
 * The Briefing tab: one column of section cards (Basic Info, Product Delivery, Hire Message, …), each read-only
 * until its Edit / Set up opens a drawer. Admin-only cards sit underneath, unchanged.
 */
@Component({
  selector: 'mbt-briefing',
  imports: [
    BriefingBasic,
    BriefingDelivery,
    BriefingDeliverables,
    BriefingCreative,
    BriefingCompliance,
    BriefingCriteria,
    BriefingScreening,
    BriefingPay,
    BriefingHireMessage,
    BriefingAdmin,
  ],
  template: `
    <div class="mba h-full min-h-0 overflow-y-auto font-(family-name:--font-fallback) text-(--fg-1)" id="bf-scroll">
      @if (tabs.focussedBrief()) {
        <div class="flex w-full flex-col gap-[18px] pb-10">
          <mbt-briefing-basic [flash]="flash() === 'basic'" />
          <mbt-briefing-delivery [flash]="flash() === 'gift'" (openLogistics)="openLogistics()" />
          <mbt-briefing-hire-message [flash]="flash() === 'hire'" />
          <mbt-briefing-deliverables [flash]="flash() === 'dist'" />
          <mbt-briefing-creative [flash]="flash() === 'docs'" />
          <mbt-briefing-compliance [flash]="flash() === 'legal'" />
          <mbt-briefing-criteria [flash]="flash() === 'crit'" />
          <mbt-briefing-screening [flash]="flash() === 'scr'" />
          <mbt-briefing-pay [flash]="flash() === 'comp'" />
          <mbt-briefing-admin class="flex flex-col gap-y-4" />
        </div>
      } @else {
        <p class="p-6 text-sm text-(--fg-3)">No brief selected</p>
      }
    </div>
  `,
  host: { class: 'block h-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BriefingComponent {
  protected readonly tabs = inject(ManageBriefTabsService);
  private readonly injector = inject(Injector);

  readonly inTabNav = output<{ tab: BriefProgressEnum; subTab: number }>();

  protected readonly flash = signal<BriefingSectionKey | null>(null);
  private flashTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Arriving from the Briefs list's "Edit basic info" highlights Basic Info, as before.
    const state = history.state as { highlightBasicInfoEdit?: boolean } | null;
    if (state?.highlightBasicInfoEdit) {
      const { highlightBasicInfoEdit: _removed, ...rest } = state;
      history.replaceState(rest, '');
      this.tabs.briefingFocus.set('basic');
    }

    // The shell's settings menu asks for a section (e.g. Hire Message): scroll to it and ring it.
    effect(() => {
      const key = this.tabs.briefingFocus() as BriefingSectionKey | null;
      if (!key || !this.tabs.focussedBrief()) {
        return;
      }
      untracked(() => {
        this.tabs.briefingFocus.set(null);
        afterNextRender(() => this.goTo(key), { injector: this.injector });
      });
    });
  }

  private goTo(key: BriefingSectionKey): void {
    document.getElementById(`bf-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.flash.set(key);
    if (this.flashTimer) {
      clearTimeout(this.flashTimer);
    }
    this.flashTimer = setTimeout(() => this.flash.set(null), FLASH_MS);
  }

  protected openLogistics(): void {
    this.inTabNav.emit({ tab: BriefProgressEnum.Logistics, subTab: 0 });
  }
}
